import React from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilePresent from "@mui/icons-material/InsertDriveFile";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { AccountTree, AccountTree as SitemapIcon, Web } from "@mui/icons-material";
import { Divider } from "@mui/material";
import { useDispatch } from "react-redux";

import wordpressLogo from "assets/images/logos/wordpress.png";
import shopifyLogo from "assets/images/logos/shopify.png";
import MDBox from "components/Basics/MDBox";
import MenuTheme from "./MenuTheme";
import MenuCategory from "./MenuCategory";

const MenuFolder = ({
	anchorEl,
	onAddFolder,
	handleEditAsset,
	onRenameFolder,
	onDeleteFolder,
	contextMenu,
	onClose,
	onAddFile,
	onAddExternalPage,
	isWritter,
	isReviewer,
	isPublisher,
	canDeleteFolder,
	canEditFolder,
	canCreateFolder,
	// Actions
	themeActions
}) => {
	const dispatch = useDispatch();
	const folder = contextMenu?.folder || {};
	const key = folder?.key || "";
	const { t, ready } = useTranslation(); // Hook to access translations
	const menuRef = React.useRef();

	// Force menu position update when translations are ready
	React.useEffect(() => {
		if (ready && menuRef.current && Boolean(anchorEl)) {
			// Small delay to ensure DOM is updated
			const timer = setTimeout(() => {
				if (menuRef.current) {
					menuRef.current.updatePosition();
				}
			}, 0);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [ready, anchorEl, t]);

	if (folder.fileType === "theme") {
		return (
			<MenuTheme
				anchorEl={anchorEl}
				onClose={onClose}
				menuRef={menuRef}
				folder={folder}
				onAddFile={onAddFile}
				handleEditAsset={handleEditAsset}
				onDeleteFolder={onDeleteFolder}
				t={t}
				themeActions={themeActions}
			/>
		);
	}

	if (folder.documentType === "answerType" && folder.type === "themeCategory") {
		return (
			<MenuCategory
				anchorEl={anchorEl}
				onClose={onClose}
				menuRef={menuRef}
				folder={folder}
				onAddFile={onAddFile}
				handleEditAsset={handleEditAsset}
				onDeleteFolder={onDeleteFolder}
				t={t}
				themeActions={themeActions}
			/>
		);
	}

	switch (key) {
		case "external_pages": {
			return (
				<Menu open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl} action={menuRef}>
					{canCreateFolder && (
						<>
							<MenuItem onClick={onAddFolder}>
								<CreateNewFolderIcon fontSize="medium" sx={{ mr: 1 }} />
								{t("SOURCES.FOLDER.addSubfolder")}
							</MenuItem>
							<Divider />
						</>
					)}
					<MenuItem onClick={() => onAddExternalPage("WebPage")}>
						<Web sx={{ mr: 1 }} fontSize="medium" /> {t("SOURCES.FOLDER.EXTERNAL_PAGE.addWebpage")}
					</MenuItem>
					<MenuItem onClick={() => onAddExternalPage("sitemap")}>
						<SitemapIcon fontSize="medium" sx={{ mr: 1 }} />
						{t("SOURCES.FOLDER.EXTERNAL_PAGE.addSitemap")}
					</MenuItem>
					<MenuItem onClick={() => onAddExternalPage("wordpressPage")}>
						<MDBox component="img" src={wordpressLogo} sx={{ width: 20, height: 20, mr: 1 }} />
						{t("SOURCES.FOLDER.EXTERNAL_PAGE.addWordpress")}
					</MenuItem>
					<MenuItem disabled onClick={() => onAddExternalPage("shopifyPage")}>
						<MDBox component="img" src={shopifyLogo} sx={{ width: 20, height: 20, mr: 1 }} />
						{t("SOURCES.FOLDER.EXTERNAL_PAGE.addShopify")}
					</MenuItem>

					{canDeleteFolder && !contextMenu?.folder?.isDefaultFolder && (
						<>
							<Divider />
							<MenuItem
								onClick={onDeleteFolder}
								style={{
									color: "red"
								}}
							>
								<DeleteIcon sx={{ mr: 1 }} fontSize="medium" /> {t("SETTINGS.delete")}
							</MenuItem>
						</>
					)}
				</Menu>
			);
		}
		case "theme": {
			return (
				<Menu open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl} action={menuRef}>
					<MenuItem onClick={onAddFile}>
						<AccountTree sx={{ mr: 1 }} fontSize="medium" /> {t("ASSETS.THEME.createTheme")}
					</MenuItem>
				</Menu>
			);
		}
		default: {
			return (
				<Menu open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl} action={menuRef}>
					{canCreateFolder && (
						<MenuItem onClick={onAddFolder}>
							<CreateNewFolderIcon sx={{ mr: 1 }} fontSize="medium" />{" "}
							{t("SOURCES.FOLDER.addSubfolder")}
						</MenuItem>
					)}

					<MenuItem onClick={() => onAddFile(folder, folder.key)}>
						<FilePresent sx={{ mr: 1 }} fontSize="medium" /> {t("SOURCES.FOLDER.addFile")}
					</MenuItem>

					{canEditFolder &&
						!folder.system &&
						!(contextMenu?.folder?.isDefaultFolder && !contextMenu?.folder?.isEditable) && (
							<MenuItem onClick={onRenameFolder}>
								<EditIcon sx={{ mr: 1 }} fontSize="medium" /> {t("SOURCES.FOLDER.rename")}
							</MenuItem>
						)}

					{canDeleteFolder && !contextMenu?.folder?.isDefaultFolder && (
						<>
							<Divider />
							<MenuItem
								onClick={onDeleteFolder}
								style={{
									color: "red"
								}}
							>
								<DeleteIcon sx={{ mr: 1 }} fontSize="medium" /> {t("SETTINGS.delete")}
							</MenuItem>
						</>
					)}
				</Menu>
			);
		}
	}
};

export default MenuFolder;
